import { useEffect, useState } from 'react';
import { ServerErrorPage } from '@/components/errors/server-error-page';
import { useLocation } from 'react-router-dom';

interface ServerError {
  status?: number;
  message?: string;
  detail?: string;
  endpoint?: string;
}

interface GlobalErrorHandlerProps {
  children: React.ReactNode;
}

export function GlobalErrorHandler({ children }: GlobalErrorHandlerProps) {
  const [error, setError] = useState<ServerError | null>(null);
  const location = useLocation();

  // Clear error when location changes
  useEffect(() => {
    setError(null);
  }, [location.pathname]);

  // Listen for server error events
  useEffect(() => {
    const handleServerError = (event: Event) => {
      const customEvent = event as CustomEvent;
      const errorDetail = customEvent.detail;

      setError({
        status: errorDetail.status,
        message: errorDetail.message,
        detail: errorDetail.detail,
        endpoint: errorDetail.endpoint,
      });
    };

    window.addEventListener('server:error', handleServerError);

    return () => {
      window.removeEventListener('server:error', handleServerError);
    };
  }, []);

  const handleRetry = () => {
    setError(null);
    window.location.reload();
  };

  const handleGoHome = () => {
    setError(null);
    window.location.href = '/dashboard';
  };

  // If there's a server error, show the error page
  if (error) {
    return (
      <ServerErrorPage
        error={error}
        onRetry={handleRetry}
        onGoHome={handleGoHome}
      />
    );
  }

  // Otherwise, render children normally
  return <>{children}</>;
}
